/**
 * R3 (ADR-032 / Wave 4) — Dashboard card showing SLA compliance %
 * per (state, alert) over the last 7 days. Reuses the existing report
 * template runner — no new endpoint, no new schedule, no new hook.
 *
 * Self-collapses when the tenant has no dwell alerts configured (the
 * report returns an empty rows array). Pairs visually with the
 * existing DwellAlertsCard (breach feed) — together they give an ops
 * manager both "what just broke" and "how are we doing overall."
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ShieldCheck, Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/api/client';
import type { ReportResultPayload } from '@/api/hooks/useReportsV2';

const ROLLING_DAYS = 7;

interface SlaRow {
  stateCode: string;
  stateName: string;
  alertCode: string;
  severity: 'info' | 'warning' | 'critical' | string;
  maxHours: number;
  enteredCount: number;
  breachedCount: number;
  compliancePct: number;
}

async function runSlaReport(fromIso: string, toIso: string): Promise<SlaRow[]> {
  try {
    const res = await api.post<ReportResultPayload>(
      `/api/v2/reports/workflow/sla-compliance/run`,
      { fromDateUtc: fromIso, toDateUtc: toIso },
    );
    return (res.data.rows ?? []).map((r) => ({
      stateCode: String(r.stateCode ?? ''),
      stateName: String(r.stateName ?? r.stateCode ?? ''),
      alertCode: String(r.alertCode ?? ''),
      severity: String(r.severity ?? 'warning'),
      maxHours: Number(r.maxHours ?? 0),
      enteredCount: Number(r.enteredCount ?? 0),
      breachedCount: Number(r.breachedCount ?? 0),
      compliancePct: Number(r.compliancePct ?? 0),
    }));
  } catch (err: unknown) {
    // 404 — workflows feature flag off / SGS deployment. Empty so the
    // card collapses silently.
    if ((err as { response?: { status?: number } }).response?.status === 404) return [];
    throw err;
  }
}

export function SlaComplianceCard() {
  const { t } = useTranslation('workflows');
  const window = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - ROLLING_DAYS * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);
  const query = useQuery({
    queryKey: ['sla-compliance', ROLLING_DAYS],
    queryFn: () => runSlaReport(window.from, window.to),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });

  if (query.isPending) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Target className="h-4 w-4" /> {t('sla.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (!query.data || query.data.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="h-4 w-4" /> {t('sla.title')}
        </CardTitle>
        <CardDescription>{t('sla.description', { days: ROLLING_DAYS })}</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {query.data.map((row) => (
            <SlaRowItem key={`${row.stateCode}/${row.alertCode}`} row={row} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function SlaRowItem({ row }: { row: SlaRow }) {
  const { t } = useTranslation('workflows');
  const pct = Math.round(row.compliancePct);
  const compliant = pct >= 95;
  const meh = pct >= 80 && pct < 95;
  return (
    <li className="flex items-start justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
      <div className="space-y-0.5 text-sm">
        <div className="flex items-center gap-2 font-medium">
          {compliant ? (
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
          ) : (
            <AlertTriangle className={`h-4 w-4 ${meh ? 'text-amber-600' : 'text-destructive'}`} />
          )}
          {row.stateName} <span className="font-normal text-muted-foreground">— {row.alertCode}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          {t('sla.detail', {
            entered: row.enteredCount,
            breached: row.breachedCount,
            hours: row.maxHours,
          })}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <span
          className={`text-lg font-semibold tabular-nums ${
            compliant ? 'text-emerald-700' : meh ? 'text-amber-700' : 'text-destructive'
          }`}
        >
          {pct}%
        </span>
        <Badge variant={compliant ? 'outline' : meh ? 'secondary' : 'destructive'} className="text-[10px]">
          {row.severity}
        </Badge>
      </div>
    </li>
  );
}
